import React, { useCallback, useState, useEffect, useRef } from 'react';
import { string, func, bool } from 'prop-types';

import { Quantity } from '@thd-olt-component-react/quantity';

import {
  buildUpdateCartRequest, generatePromoCartItemsObj, buildAddToCartRequest
} from '../../utils/promo-cart-utils';
import { usePromoModel } from '../../contexts/PromoModelProvider';
import { usePromoCart } from '../../contexts/PromoCartProvider';

export const PromoQuantity = ({ itemId, setWarningText, showWarningState }) => {
  const { allFulfillableProductsAndAnchor, storeId, storeZip } = usePromoModel();
  const {
    promoCartItemsObj,
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
    freezeCart,
  } = usePromoCart();

  const product = allFulfillableProductsAndAnchor.get(itemId);
  const inCartQuantity = promoCartItemsObj?.[itemId]?.quantity || 0;

  const [quantityTried, setQuantityTried] = useState(null);
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const onCartQuantityChange = useCallback(({ quantity }) => {
    if (product && promoCartItemsObj?.[itemId]?.quantity !== quantity) {

      // quantityLimit comes back as 0 when there is no limit
      const { info } = product;
      const { quantityLimit } = info || {};
      const hasAProductLimit = quantityLimit > 0;
      const maxPurchasableQuantity = hasAProductLimit ? Math.min(quantityLimit, quantity) : quantity;

      const store = { storeZip, storeId };
      const { CartRequest: updateCartRequest } = buildUpdateCartRequest({
        product,
        promoItemModel: promoCartItemsObj[itemId],
        desiredQuantity: maxPurchasableQuantity,
        store
      });

      const unFreezeCart = freezeCart();
      const updatePromise = updateCartRequest.itemDetails.length
        ? updateCart(updateCartRequest)
        : Promise.resolve(CartModel);

      updatePromise
        .then((newCartModel) => {
          const promoCartItems = generatePromoCartItemsObj({
            itemModels: newCartModel?.itemModels,
            allFulfillableProductsAndAnchor
          });
          const promoItemModel = promoCartItems[itemId];
          const desiredQuantity = maxPurchasableQuantity - (promoItemModel?.quantity || 0);

          // If after updating the already added fulfillments there is still more quantities to fulfill, add to cart
          if (desiredQuantity > 0) {
            const { CartRequest: addToCartRequest } = buildAddToCartRequest({
              desiredQuantity, promoItemModel, product, store
            });

            if (addToCartRequest.itemDetails.length) return addToCart(addToCartRequest);
          }

          return Promise.resolve(newCartModel);
        })
        .then((newCartModel) => {
          if (isMounted.current) {
            const promoCartItems = generatePromoCartItemsObj({
              itemModels: newCartModel?.itemModels,
              allFulfillableProductsAndAnchor
            });
            const promoItemModel = promoCartItems[itemId];

            if (maxPurchasableQuantity > promoItemModel?.quantity) {
              // If quantity requested is larger than quantity in cart, there must have been limited fulfillment
              setWarningText(` There's only ${promoItemModel.quantity} in stock`);
            } else if (hasAProductLimit && quantityLimit < quantity) {
              // If limit on product is less than initial desired quantity, we know that there is a product limit
              setWarningText(` Limit ${quantityLimit} per order`);
            } else {
              setWarningText('');
            }
          }
        })
        .finally(() => {
          unFreezeCart();
          if (isMounted.current) setQuantityTried(!quantityTried);
        });
    }
  }, [
    product,
    promoCartItemsObj,
    itemId,
    storeId,
    storeZip,
    updateCart,
    CartModel,
    allFulfillableProductsAndAnchor,
    addToCart,
    quantityTried,
    setWarningText,
    freezeCart
  ]);

  return (
    <Quantity
      key={quantityTried}
      wideView
      value={inCartQuantity}
      onBlurValue={onCartQuantityChange}
      minimumQuantity={0}
      disabled={isCartLoading}
      showWarningState={showWarningState}
      noRightMargin
    />
  );
};

PromoQuantity.displayName = 'PromoQuantity';

PromoQuantity.propTypes = {
  itemId: string.isRequired,
  setWarningText: func,
  showWarningState: bool
};

PromoQuantity.defaultProps = {
  setWarningText: () => {},
  showWarningState: false
};
